import { rgba } from 'polished';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Input } from '../form/controls/Input';
import { AddIcon, CloseIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Popover } from './Popover';
import { Label, Paragraph, Subheading } from './Text';
import { Button } from './button/Button';

const Container = styled(Flex)`
	border-radius: 5px;
	background: ${rgba(tokens.border.subtle, 0.25)};
	flex-wrap: wrap;
`;

const Item = styled(Flex)`
	border-radius: 2px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.25)},
		0px 2px 4px ${rgba(tokens.text.secondary, 0.25)};
	height: 32px;
`;

const ItemTitle = styled(Flex)`
	padding: 4px 8px;
	align-items: center;
`;

const AddButton = styled(Box)<{ enabled?: boolean }>`
	background: ${tokens.border.subtle};
	width: 32px;
	height: 32px;
	padding: 4px;
	border-radius: 16px;

	svg {
		display: block;
		width: 24px;
		height: 24px;
	}

	${(props) =>
		props.enabled !== false &&
		`
		cursor: pointer;
		
		&:hover {
			background: ${props.theme.colors.lightBlueDark};

			&:active {
				background: ${tokens.text.secondary};

				svg {
					path {
						fill: ${props.theme.colors.white};
					}
				}
			}
		}`}
`;

const DeleteButton = styled(Flex)`
	border-left: 1px solid ${tokens.border.subtle};
	align-items: center;
	justify-content: center;
	flex-grow: 0;
	flex-shrink: 0;
	width: 32px;
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}

	&:hover {
		background: ${rgba(tokens.border.subtle, 0.25)};

		&:active {
			background: ${rgba(tokens.border.subtle, 0.5)};
		}
	}
`;

function MultiInputValue(props: { title: string; onDelete?: () => void }) {
	return (
		<Item ml={2} mb={2}>
			<ItemTitle>{props.title}</ItemTitle>
			{props.onDelete && (
				<DeleteButton onClick={() => props.onDelete?.()}>
					<CloseIcon />
				</DeleteButton>
			)}
		</Item>
	);
}

export function MultiInput(props: {
	required?: boolean;
	values: string[];
	title?: string;
	description?: string;
	placeholder?: string;
	onChange: (values: string[]) => void;
}) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	const [value, setValue] = useState('');

	function add() {
		const set = new Set(props.values);
		set.add(value);
		props.onChange(Array.from(set).sort());
		setValue('');
		setVisible(false);
	}

	return (
		<Flex alignItems="center">
			<Container alignItems="center" p={2} pl={0} pb={0} flexGrow={1} flexShrink={1}>
				{props.values.map((value, index) => (
					<MultiInputValue
						key={index}
						title={value}
						onDelete={
							!props.required || props.values.length > 1
								? () => {
										const values = props.values.slice();
										values.splice(index, 1);
										props.onChange(values);
									}
								: undefined
						}
					/>
				))}
				{props.values.length === 0 && (
					<Label ml={2} mb={2} style={{ lineHeight: '32px' }}>
						{t('common.multiSelect.empty')}
					</Label>
				)}
			</Container>
			<Box flexGrow={0} flexShrink={0} ml={2}>
				<Popover.Target
					visible={visible}
					onDismiss={() => setVisible(false)}
					contents={
						<Box p={3} maxWidth={320}>
							{(props.title || props.description) && (
								<Box mb={2}>
									{props.title && <Subheading>{props.title}</Subheading>}
									{props.description && <Paragraph>{props.description}</Paragraph>}
								</Box>
							)}
							<Flex>
								<Input
									flexGrow={1}
									value={value}
									onChange={setValue}
									autoFocus
									small
									mr={2}
									placeholder={props.placeholder}
									onKeyDown={(event) => {
										if (event.key !== 'Enter') {
											return;
										}

										add();
									}}
								/>
								<Button small title={t('common.multiSelect.add')} onClick={add} />
							</Flex>
						</Box>
					}
				>
					<AddButton onClick={() => setVisible(true)}>
						<AddIcon />
					</AddButton>
				</Popover.Target>
			</Box>
		</Flex>
	);
}
